import { Component, HostListener, inject, OnInit } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { HeaderComponent } from './shared/components/header/header.component';
import { LANGS } from './shared/constants/lang.constants';
import { ChatbotComponent } from './pages/chatbot/chatbot.component';
import { filter } from 'rxjs';
import { LoggerService } from './shared/services/logger.service';
import { MatDialog } from '@angular/material/dialog';
import { OtpDialogModel } from './shared/interfaces/dialogs/otp-dialog.model';
import { OtpDialogComponent } from './shared/components/dialogs/otp-dialog/otp-dialog.component';
import { ForgotPasswordDialogComponent } from './shared/components/dialogs/forgot-password-dialog/forgot-password-dialog.component';
import { SingleActionDialogComponent } from './shared/components/dialogs/single-action-dialog/single-action-dialog.component';
import { DuoActionDialogComponent } from './shared/components/dialogs/duo-action-dialog/duo-action-dialog.component';
import { BmiDialogComponent } from './shared/components/dialogs/bmi-dialog/bmi-dialog.component';
import { SharedStorageService } from './shared/services/shared-storage.service';
import { AuthService } from './shared/services/auth.service';
import { IdleTimeoutService } from './shared/services/idle-timeout.service';


@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, HeaderComponent, ChatbotComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  hideHeader = false;

  constructor(
    private dialog: MatDialog,
    private translate: TranslateService,
    private router: Router,
    private logger: LoggerService,
    private sharedStorageService: SharedStorageService,
    private authService: AuthService,
    private idleTimeoutService: IdleTimeoutService,

  ) {
    (window as any).openOtp = (data: OtpDialogModel) =>
      this.dialog.open(OtpDialogComponent, { data });

    (window as any).openForgot = (cfg: any = {}) =>
      this.dialog.open(ForgotPasswordDialogComponent, { ...cfg });

    (window as any).openSingle = (data: any = {}, cfg: any = {}) => {
      const ref = this.dialog.open(SingleActionDialogComponent, {
        data,
        ...cfg,
      });
      if (!data.action) {
        ref.componentInstance.data.action = () => ref.close();
      }
      return ref;
    };

    (window as any).openDuo = (data: any = {}, cfg: any = {}) => {
      const ref = this.dialog.open(DuoActionDialogComponent, { data, ...cfg });
      if (!data.leftButtonAction) data.leftButtonAction = () => ref.close();
      if (!data.rightButtonAction) data.rightButtonAction = () => ref.close();
      return ref;
    };

    (window as any).openBmi = (data: any = {}, cfg: any = {}) => {
      const ref = this.dialog.open(BmiDialogComponent, { data, ...cfg });
      return ref;
    };
  }

  ngOnInit(): void {
    // 紀錄 App 啟動
    this.logger.push('► App started');

    const loginStatus = this.sharedStorageService.get('loginStatus');

    // 如果已登入，則開始監控閒置時間
    if (loginStatus === 'memberLogin') {
      this.idleTimeoutService.startWatching();
    }

    this.ensureRefreshSession();

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        // 檢查當前路由是否應該隱藏 header
        const routeData =
          this.router.routerState.root.firstChild?.snapshot.data;
        this.hideHeader = routeData?.['hideHeader'] === true;
      });
  }

  ngOnDestroy() {
    this.logger.push('◄ App destroyed');
  }

  // 區分重新整理與關閉分頁，透過sessionId
  private ensureRefreshSession() {
    let sessionId = sessionStorage.getItem('sessionId');
    if (!sessionId) {
      sessionId = crypto.randomUUID();
      sessionStorage.setItem('sessionId', sessionId);
    }
    const lastSessionId = this.sharedStorageService.get('lastSessionId');

    // 檢查是否為重新整理
    const navigationEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
    const isPageRefresh = navigationEntries.length > 0 && navigationEntries[0].type === 'reload';

    console.log('Session 檢查:', {
      currentSessionId: sessionId,
      lastSessionId: lastSessionId,
      isPageRefresh: isPageRefresh,
      shouldClear: !isPageRefresh && lastSessionId && lastSessionId !== sessionId
    });

    // 如果不是重新整理且 sessionId 不同時才清除資料
    if (!isPageRefresh && lastSessionId && lastSessionId !== sessionId) {
      console.log('檢測到新的瀏覽器 session，清除登入狀態和資料');
      
      this.authService.clearToken();
      this.sharedStorageService.clear();
      
      // 強制清除特定的髒資料
      this.sharedStorageService.remove('myReservation');
      this.sharedStorageService.remove('loginStatus');
      this.sharedStorageService.remove('savedQuestionnaireData');
      this.sharedStorageService.remove('createMemberRequest');
      
      this.clearBrowserCache();
      window.location.reload();
    }

    this.sharedStorageService.set('lastSessionId', sessionId);
  }

  // 清除瀏覽器快取
  private clearBrowserCache(): void {
    // 清除所有類型的儲存
    if (typeof window !== 'undefined') {
      // 清除 localStorage
      // localStorage.clear();
      this.sharedStorageService.clear();
      
      // 清除 sessionStorage
      sessionStorage.clear();
      
      // 清除 IndexedDB (如果有使用)
      if ('indexedDB' in window) {
        indexedDB.databases().then(databases => {
          databases.forEach(db => {
            if (db.name) indexedDB.deleteDatabase(db.name);
          });
        });
      }
      
      // 清除 Service Worker 快取 (如果有使用)
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistrations().then(registrations => {
          registrations.forEach(registration => registration.unregister());
        });
      }
    }
  }
}
